import { relations, sql } from 'drizzle-orm';
import { boolean, decimal, index, integer, jsonb, pgEnum, pgTable, text, uuid } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { user } from './auth.js';
import { applicationForms, cancerTypes, diagnoses } from './basic-data.js';
import { drugCatalogItems } from './drug-catalog.js';
import { DoseCalcModeActive, doseCalcModeActiveEnum, formTypeEnum, FormType, preparationTypeEnum } from './enums.js';
import { volumeModeEnum } from './enums.js';
import { prescribedNames } from './products.js';
import { timestamps } from './utils/index.js';
import { resourceStatus } from './utils/resource-status.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
export const CancerStages = z.enum(['STAGE_0', 'STAGE_I', 'STAGE_II', 'STAGE_III', 'STAGE_IV', 'RECCURENT', 'N/A']);
export const cancerStagesEnum = pgEnum('cancer_stages', [
    CancerStages.enum.STAGE_0,
    CancerStages.enum.STAGE_I,
    CancerStages.enum.STAGE_II,
    CancerStages.enum.STAGE_III,
    CancerStages.enum.STAGE_IV,
    CancerStages.enum.RECCURENT,
    CancerStages.enum['N/A'],
]);
export const protocols = pgTable('protocols', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    name: text('name').notNull(),
    diagnosisId: uuid('diagnosis_id').references(() => diagnoses.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    cancerTypeId: uuid('cancer_type_id').references(() => cancerTypes.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    stage: cancerStagesEnum('stage'),
    isTrial: boolean('is_trial').notNull().default(false),
    status: resourceStatus('status').default('active'),
    totalCycles: integer('total_cycles').notNull(),
    daysRepetition: integer('days_repetition').notNull(),
    notes: text('notes'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_protocols_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const protocolTherapySequence = pgTable('protocol_therapy_sequence', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    protocolId: uuid('protocol_id')
        .notNull()
        .references(() => protocols.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    isNote: boolean('is_note').notNull().default(false),
    note: text('note'),
    day: integer('day').notNull(),
    time: text('time').notNull(),
    carrierSolutionId: uuid('carrier_solution_id').references(() => drugCatalogItems.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    applicationFormId: uuid('application_form_id').references(() => applicationForms.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    duration: integer('duration'),
    volume: integer('volume'),
    volumeMode: volumeModeEnum('volume_mode'),
    therapyNote: text('therapy_note'),
    pharmacyNote: text('pharmacy_note'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_protocol_therapy_sequence_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const protocolTherapySequenceMedicine = pgTable('protocol_therapy_sequence_medicine', {
    id: uuidv7('id').primaryKey(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => protocolTherapySequence.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    activeSubstanceId: uuid('active_substance_id').references(() => drugCatalogItems.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
    }),
    prescribedNameId: uuid('prescribed_name_id').references(() => prescribedNames.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
    }),
    autIdem: boolean('aut_idem').notNull().default(false),
    formType: formTypeEnum('form_type').default(FormType.enum.OTHER),
    calculationMethod: doseCalcModeActiveEnum('calculation_method').default(DoseCalcModeActive.enum.ABSOLUTE),
    regularDose: decimal('regular_dose', { mode: 'number' }).notNull(),
    precisionDecimals: integer('precision_decimals').default(0),
    preparationType: preparationTypeEnum('preparation_type'),
    notes: text('notes'),
    ageRanges: jsonb('age_ranges').$type(), // JSON stringified array
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_protocol_therapy_sequence_medicine_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const protocolsRelations = relations(protocols, ({ one, many }) => ({
    diagnosis: one(diagnoses, {
        fields: [protocols.diagnosisId],
        references: [diagnoses.id],
    }),
    created: one(user, {
        fields: [protocols.createdBy],
        references: [user.id],
    }),
    cancerType: one(cancerTypes, {
        fields: [protocols.cancerTypeId],
        references: [cancerTypes.id],
    }),
    therapySequence: many(protocolTherapySequence),
}));
export const protocolTherapySequenceRelations = relations(protocolTherapySequence, ({ one, many }) => ({
    protocol: one(protocols, {
        fields: [protocolTherapySequence.protocolId],
        references: [protocols.id],
    }),
    carrierSolution: one(drugCatalogItems, {
        fields: [protocolTherapySequence.carrierSolutionId],
        references: [drugCatalogItems.id],
    }),
    applicationForm: one(applicationForms, {
        fields: [protocolTherapySequence.applicationFormId],
        references: [applicationForms.id],
    }),
    medicine: many(protocolTherapySequenceMedicine),
    created: one(user, {
        fields: [protocolTherapySequence.createdBy],
        references: [user.id],
    }),
}));
export const protocolTherapySequenceMedicineRelations = relations(protocolTherapySequenceMedicine, ({ one }) => ({
    therapySequence: one(protocolTherapySequence, {
        fields: [protocolTherapySequenceMedicine.therapySequenceId],
        references: [protocolTherapySequence.id],
    }),
    activeSubstance: one(drugCatalogItems, {
        fields: [protocolTherapySequenceMedicine.activeSubstanceId],
        references: [drugCatalogItems.id],
    }),
    prescribedName: one(prescribedNames, {
        fields: [protocolTherapySequenceMedicine.prescribedNameId],
        references: [prescribedNames.id],
    }),
    created: one(user, {
        fields: [protocolTherapySequenceMedicine.createdBy],
        references: [user.id],
    }),
}));
//# sourceMappingURL=protocols.js.map